import { MessageBubble } from '../Chat/MessageBubble'
import { TypingIndicator } from '../Chat/TypingIndicator'
import { useChat } from '../Chat/hooks'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { AgentStep, ChatMessage } from '../Chat/types'
import { AnimatePresence, motion } from 'motion/react'
import { ChevronDown, ChevronRight, Wrench, CheckCircle2, Brain, Loader2, Sparkles } from 'lucide-react'

// Agent step display component - auto-expands when streaming, collapsible by user
function AgentStepDisplay({
  step,
  isLatest,
  isStreaming,
}: {
  step: AgentStep
  isLatest: boolean
  isStreaming: boolean
}) {
  // Auto-expand when this step is the latest and actively streaming
  const shouldAutoExpand = isLatest && isStreaming
  const [userCollapsed, setUserCollapsed] = useState(false)
  const isExpanded = shouldAutoExpand ? !userCollapsed : !userCollapsed && isLatest

  const getStepIcon = () => {
    switch (step.stepType) {
      case 'tool_call':
        return isLatest && isStreaming ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" />
        ) : (
          <Wrench className="h-3.5 w-3.5 text-blue-500" />
        )
      case 'tool_result':
        return <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
      case 'thinking':
        return isLatest && isStreaming ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-purple-500" />
        ) : (
          <Brain className="h-3.5 w-3.5 text-purple-500" />
        )
      case 'agent_step':
        return isLatest && isStreaming ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-500" />
        ) : (
          <Sparkles className="h-3.5 w-3.5 text-amber-500" />
        )
      default:
        return <Brain className="h-3.5 w-3.5 text-gray-500" />
    }
  }

  const getStepTitle = () => {
    switch (step.stepType) {
      case 'tool_call':
        return `${isLatest && isStreaming ? 'Calling' : 'Called'}: ${step.toolName}`
      case 'tool_result':
        return `Result: ${step.toolName}`
      case 'thinking':
        return isLatest && isStreaming ? 'Thinking...' : 'Thought process'
      case 'agent_step':
        return `Step ${step.stepNumber}${isLatest && isStreaming ? ' (working...)' : ''}`
      default:
        return 'Processing...'
    }
  }

  const hasDetails = step.toolArgs || step.toolResult

  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.15 }}
      className={`my-1 rounded-md border ${
        isLatest && isStreaming ? 'border-primary/30 bg-primary/5 shadow-sm' : 'border-border/50 bg-muted/30'
      }`}
    >
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-muted/50"
        onClick={() => {
          if (hasDetails) {
            setUserCollapsed(!userCollapsed ? true : false)
          }
        }}
        disabled={!hasDetails}
      >
        {getStepIcon()}
        <span className="flex-1 font-medium text-foreground/80">{getStepTitle()}</span>
        {hasDetails && (
          <span className="text-muted-foreground">
            {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          </span>
        )}
      </button>
      <AnimatePresence initial={false}>
        {isExpanded && hasDetails && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="border-t border-border/30 px-3 py-2">
              {step.toolArgs && (
                <div className="mb-2">
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Arguments
                  </div>
                  <pre className="bg-offwhite/50 max-h-32 overflow-auto rounded p-2 text-[11px] text-foreground/70">
                    {JSON.stringify(step.toolArgs, null, 2)}
                  </pre>
                </div>
              )}
              {step.toolResult && (
                <div>
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Result
                  </div>
                  <pre className="bg-offwhite/50 max-h-48 overflow-auto whitespace-pre-wrap rounded p-2 text-[11px] text-foreground/70">
                    {step.toolResult}
                  </pre>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

// Agent steps container - auto-expands during streaming, user can collapse
function AgentStepsContainer({ steps, isActive }: { steps: AgentStep[]; isActive: boolean }) {
  // Track if user manually collapsed - if not, auto-expand during streaming
  const [userCollapsed, setUserCollapsed] = useState(false)
  const prevStepsCountRef = useRef(steps.length)

  // Auto-expand when new steps arrive (unless user collapsed)
  useEffect(() => {
    if (steps.length > prevStepsCountRef.current && !isActive) {
      // Don't auto-expand on completion
    } else if (steps.length > prevStepsCountRef.current) {
      // New step arrived while streaming - ensure expanded
      setUserCollapsed(false)
    }
    prevStepsCountRef.current = steps.length
  }, [steps.length, isActive])

  // When streaming starts, auto-expand
  useEffect(() => {
    if (isActive) {
      setUserCollapsed(false)
    }
  }, [isActive])

  if (!steps || steps.length === 0) return null

  const isExpanded = isActive ? !userCollapsed : !userCollapsed

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={`mb-3 rounded-lg border ${
        isActive ? 'border-primary/20 bg-primary/5' : 'border-border/40 bg-muted/20'
      } p-2`}
    >
      <button
        type="button"
        className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs text-muted-foreground hover:bg-muted/50 hover:text-foreground"
        onClick={() => setUserCollapsed(!userCollapsed)}
      >
        {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        {isActive ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
        ) : (
          <Brain className="h-3.5 w-3.5 text-purple-500" />
        )}
        <span className="font-medium">
          {isActive ? (
            <span className="text-primary">Working on your request...</span>
          ) : (
            <span>
              {steps.length} step{steps.length !== 1 ? 's' : ''} completed
            </span>
          )}
        </span>
      </button>
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-1 space-y-1 pl-2">
              {steps.map((step, idx) => (
                <AgentStepDisplay
                  key={`${step.stepNumber}-${step.stepType}-${idx}`}
                  step={step}
                  isLatest={idx === steps.length - 1}
                  isStreaming={isActive}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

// Thinking display component - shows streaming thinking content
function ThinkingDisplay({
  thinking,
  isStreaming,
  onCollapse,
  isCollapsed,
}: {
  thinking: string
  isStreaming: boolean
  onCollapse: () => void
  isCollapsed: boolean
}) {
  const contentRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom during streaming
  useEffect(() => {
    if (isStreaming && contentRef.current && !isCollapsed) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight
    }
  }, [thinking, isStreaming, isCollapsed])

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={`mb-3 rounded-lg border ${
        isStreaming ? 'border-purple-200 bg-purple-50/50' : 'border-border/40 bg-muted/20'
      } overflow-hidden`}
    >
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2 text-xs text-muted-foreground hover:bg-muted/50 hover:text-foreground"
        onClick={onCollapse}
      >
        {isCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        {isStreaming ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-purple-500" />
        ) : (
          <Brain className="h-3.5 w-3.5 text-purple-500" />
        )}
        <span className="font-medium">
          {isStreaming ? <span className="text-purple-600">Thinking...</span> : <span>Thought process</span>}
        </span>
      </button>
      <AnimatePresence initial={false}>
        {!isCollapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <div ref={contentRef} className="max-h-48 overflow-auto border-t border-border/30 px-3 py-2">
              <pre className="whitespace-pre-wrap text-xs text-muted-foreground">
                {thinking}
                {isStreaming && <span className="ml-0.5 inline-block h-3 w-1 animate-pulse bg-purple-400" />}
              </pre>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

// Message list component
export function LiveChatMessageList(props?: {
  messages?: ChatMessage[]
  isLoading?: boolean
  thinkingByMessageId?: Record<string, string>
  responseStartedByMessageId?: Record<string, boolean>
  agentStepsByMessageId?: Record<string, AgentStep[]>
}) {
  const { messages, isLoading } = useChat()
  const finalMessages = props?.messages ?? messages
  const finalIsLoading = props?.isLoading ?? isLoading
  const thinkingByMessageId = useMemo(() => props?.thinkingByMessageId ?? {}, [props?.thinkingByMessageId])
  const responseStartedByMessageId = useMemo(
    () => props?.responseStartedByMessageId ?? {},
    [props?.responseStartedByMessageId]
  )
  const agentStepsByMessageId = useMemo(() => props?.agentStepsByMessageId ?? {}, [props?.agentStepsByMessageId])

  // Track user's collapse state for thinking sections by message ID
  const [thinkingCollapsedById, setThinkingCollapsedById] = useState<Record<string, boolean>>({})

  // Determine the latest assistant message (current streaming target)
  const latestAssistantId = useMemo(() => {
    const latestAssistant = [...finalMessages].reverse().find((m) => m.role === 'assistant')
    return latestAssistant?.id
  }, [finalMessages])

  // Check if there's active thinking content being streamed
  const hasActiveThinking = useMemo(() => {
    if (!latestAssistantId) return false
    const t = thinkingByMessageId[latestAssistantId]
    const rs = responseStartedByMessageId[latestAssistantId]
    return Boolean(t && t.trim().length > 0 && !rs)
  }, [latestAssistantId, thinkingByMessageId, responseStartedByMessageId])

  // Check if there are active agent steps (not yet responded)
  const hasActiveAgentSteps = useMemo(() => {
    if (!latestAssistantId) return false
    const steps = agentStepsByMessageId[latestAssistantId]
    const rs = responseStartedByMessageId[latestAssistantId]
    return Boolean(steps && steps.length > 0 && !rs)
  }, [latestAssistantId, agentStepsByMessageId, responseStartedByMessageId])

  // Auto-expand thinking when streaming starts for a new message
  useEffect(() => {
    if (latestAssistantId && hasActiveThinking) {
      // Ensure thinking is expanded when streaming starts (unless user collapsed it)
      setThinkingCollapsedById((prev) => {
        if (prev[latestAssistantId] === undefined) {
          return { ...prev, [latestAssistantId]: false }
        }
        return prev
      })
    }
  }, [latestAssistantId, hasActiveThinking])

  if (finalMessages.length === 0) {
    return null
  }

  return (
    <div className="flex-1 p-4">
      {finalMessages.map((message) => {
        const thinking = thinkingByMessageId[message.id]
        const hasThinking = Boolean(thinking?.trim())
        const responseStarted = Boolean(responseStartedByMessageId[message.id])
        const agentSteps = agentStepsByMessageId[message.id] || []
        const hasAgentSteps = agentSteps.length > 0

        // Is this message currently streaming thinking?
        const isStreamingThinking =
          message.role === 'assistant' && message.id === latestAssistantId && hasThinking && !responseStarted

        // Is this message currently streaming agent steps?
        const isStreamingAgentSteps =
          message.role === 'assistant' && message.id === latestAssistantId && hasAgentSteps && !responseStarted

        // Should we show thinking for this message?
        const showThinking = message.role === 'assistant' && hasThinking

        // Is thinking collapsed for this message?
        const isThinkingCollapsed = thinkingCollapsedById[message.id] ?? false

        return (
          <div key={message.id}>
            {/* Agent Steps Display - auto-shows during streaming */}
            {message.role === 'assistant' && hasAgentSteps && (
              <AgentStepsContainer steps={agentSteps} isActive={isStreamingAgentSteps} />
            )}

            {/* Thinking Display - auto-shows during streaming, collapsible */}
            {showThinking && !hasAgentSteps && (
              <ThinkingDisplay
                thinking={thinking}
                isStreaming={isStreamingThinking}
                isCollapsed={isStreamingThinking ? isThinkingCollapsed : isThinkingCollapsed}
                onCollapse={() =>
                  setThinkingCollapsedById((prev) => ({
                    ...prev,
                    [message.id]: !prev[message.id],
                  }))
                }
              />
            )}

            {/* Message Bubble - only show if there's content */}
            {(message.role === 'user' || (message.content && message.content.trim().length > 0)) && (
              <MessageBubble message={message} />
            )}
          </div>
        )
      })}
      {/* Extra bottom padding while actively thinking or agent is working */}
      {(hasActiveThinking || hasActiveAgentSteps) && <div className="h-6" />}
      {finalIsLoading && <TypingIndicator />}
    </div>
  )
}
